import type {
  FieldValue,
  FileFieldValue,
  FormField
} from '@elfsight/widget-form-builder/widget';
import { EXCLUDED_SUBMIT_FIELD_TYPES, FIELDS_WITH_SELECT } from '../constants';
import { findInOptions } from './findInOptions';

function getSelectValue(field: FormField, value: string | number) {
  const { type: fieldType, options } = field;
  const option = findInOptions(value, options);
  if (!option) {
    return value;
  }

  const { name: optionName } = option;
  if (fieldType === 'choice') {
    return optionName || value;
  }

  if (fieldType === 'imageChoice' && 'index' in option) {
    return optionName || option.index;
  }

  return optionName;
}

function getFileValue(rawValue: FileFieldValue) {
  return rawValue.reduce<string[]>((acc, { uploaded }) => {
    if (uploaded) {
      acc.push(uploaded);
    }

    return acc;
  }, []);
}

function isFileFieldValue(fieldValue: unknown): fieldValue is FileFieldValue {
  return (
    Array.isArray(fieldValue) &&
    !fieldValue.some((value) => !(value instanceof File))
  );
}

export function getProcessedSubmitFields(
  derivedFields: Record<string, FormField | { value?: FieldValue }>,
  fields: FormField[]
) {
  const processValue = (field: FormField, rawValue?: FieldValue) => {
    if (!rawValue) {
      return;
    }

    if (isFileFieldValue(rawValue)) {
      return getFileValue(rawValue);
    }

    const { type } = field;
    if (rawValue && FIELDS_WITH_SELECT.includes(type)) {
      return Array.isArray(rawValue)
        ? rawValue
            .map((item) => String(getSelectValue(field, item) ?? ''))
            .filter(Boolean)
        : getSelectValue(field, rawValue);
    }

    return rawValue;
  };

  return fields.reduce<Record<string, Partial<FormField>>>((acc, field) => {
    const { fieldID, name, type, id } = field;
    if (EXCLUDED_SUBMIT_FIELD_TYPES.includes(type)) {
      return acc;
    }

    const { value: rawValue } = derivedFields[id];
    const value = processValue(field, rawValue);

    acc[id] = { fieldID, name, type, value };

    return acc;
  }, {});
}
